// src/AnimationView/engine/entities/Character.ts
import Phaser from "phaser";
import { GameObject } from "./Object";
import type {
  CharacterState,
  TAnimationConfig,
} from "../../../../models/objects/sprite";
import type { CharacterConfig } from "../../types/config";

/**
 * Représente un personnage 2D (logique + affichage Phaser).
 * API publique conservée.
 */
const normKey = (s?: string) => String(s ?? "").trim().replace(/\s+/g, "_");

export class Character {
  private adminMode: boolean;

  // --- Logique ---
  public id: string;
  public idPhaser: string;
  private scene?: Phaser.Scene;
  public x: number;
  public y: number;
  public width: number;
  public height: number;
  public angleDirection: number;

  // --- Animation ---
  public currentState: CharacterState = "idle";
  public image_url: string;
  public animations: TAnimationConfig[];

  // --- Affichage Phaser ---
  public sprite?: Phaser.Types.Physics.Arcade.SpriteWithDynamicBody;
  private directionIndicator?: Phaser.GameObjects.Image;
  private dotTextureKey?: string;
  private arrowTextureKey?: string;

  // Admin label
  private adminLabel?: Phaser.GameObjects.Text;

  // Objets portés
  private carriedObject: GameObject[] = [];

  // Déplacement/état
  private isMoving = false;
  private isColliding = false;
  private currentMovementCancel: (() => void) | null = null;
  private movementQueue: Array<{ unitNumber: number; resolve: () => void; reject: (err: any) => void }> = [];

  // Auto-pilote
  private autoPilotEnabled = false;
  private autoPilotTimer?: Phaser.Time.TimerEvent;

  // Bound handlers for cleanup
  private boundUpdateForLabel?: () => void;
  private _isDestroying = false;

  private placeholderTextureKey?: string;
  private textureAddHandler?: (key: string, texture: Phaser.Textures.Texture) => void;


  constructor(adminMode: boolean, config: CharacterConfig) {
    this.adminMode = adminMode;
    this.id = config.id;
    this.idPhaser = normKey(
      config.idPhaser ?? config.id ?? (config as any).name ?? "character"
    );
    this.x = config.x;
    this.y = config.y;
    this.width = config.width;
    this.height = config.height;
    this.angleDirection = 0;

    this.image_url = (config as any).image_url;
    this.animations = (config as any).animations || [];
  }

  /** Important for cleanup when removing the character */
  public destroy(fromSprite = false) {
    if (this._isDestroying) return;
    this._isDestroying = true;

    this.stopAutoPilot();
    this.stopMovement();

    if (this.scene && this.textureAddHandler) {
      this.scene.textures.off(Phaser.Textures.Events.ADD, this.textureAddHandler);
      this.textureAddHandler = undefined;
    }

    if (this.scene) {
      if (this.boundUpdateForLabel) {
        this.scene.events.off("update", this.boundUpdateForLabel);
        this.boundUpdateForLabel = undefined;
      }
    }

    this.directionIndicator?.destroy();
    this.adminLabel?.destroy();

    // Optionnel : nettoyer les textures générées
    if (this.scene && this.dotTextureKey && this.scene.textures.exists(this.dotTextureKey)) {
      this.scene.textures.remove(this.dotTextureKey);
    }
    if (this.scene && this.arrowTextureKey && this.scene.textures.exists(this.arrowTextureKey)) {
      this.scene.textures.remove(this.arrowTextureKey);
    }

    if (!fromSprite) {
      this.sprite?.destroy(true);
    }
    if (this.scene && this.placeholderTextureKey && this.scene.textures.exists(this.placeholderTextureKey)) {
      this.scene.textures.remove(this.placeholderTextureKey);
    }
    this.sprite = undefined;
    this.scene = undefined;
  }

  private setMovingState(moving: boolean) {
    this.isMoving = moving;
    if (!this.directionIndicator) return;

    const textureKey = moving ? this.arrowTextureKey : this.dotTextureKey;
    if (textureKey) this.directionIndicator.setTexture(textureKey);

    // Avoid stacking tweens
    if (moving) {
      this.directionIndicator.setScale(1);
      this.scene?.tweens.add({
        targets: this.directionIndicator,
        scale: 1.2,
        duration: 150,
        yoyo: true,
        repeat: 0,
      });
    } else {
      this.directionIndicator.setScale(1);
    }

    this.updateDirectionIndicator();
  }

  public stopMovement() {
    if (this.currentMovementCancel) {
      this.currentMovementCancel();
      this.currentMovementCancel = null;
    }
    this.setMovingState(false);
  }

  /** Retourne une clé de texture sûre.
 *  - si la vraie texture existe, renvoie this.idPhaser
 *  - sinon, génère une petite texture placeholder et s'abonne à l'événement
 *    d'ajout de la vraie texture pour basculer automatiquement.
 */
  // ---- Dans Character.ts ----
  private resolveTextureOrPlaceholder(scene: Phaser.Scene): string {
    // 0) texture déjà chargée sous la bonne clé
    if (scene.textures.exists(this.idPhaser)) return this.idPhaser;

    // 1) si elle existe sous un alias connu, créer un alias immédiat vers idPhaser 
    const tryAliasKeys = [
      `char_${this.idPhaser}`,
      `sprite_${this.idPhaser}`,
      `obj_${this.idPhaser}`,
      `obstacle_${this.idPhaser}`,
    ];

    for (const alias of tryAliasKeys) {
      if (scene.textures.exists(alias)) {
        const tex = scene.textures.get(alias);
        const src = tex.getSourceImage() as HTMLImageElement | HTMLCanvasElement | undefined;

        if (src && !scene.textures.exists(this.idPhaser)) {
          if (src instanceof HTMLImageElement) {
            scene.textures.addImage(this.idPhaser, src);
          } else if (src instanceof HTMLCanvasElement) {
            // Phaser a une API dédiée pour les canvas
            (scene.textures as any).addCanvas
              ? (scene.textures as any).addCanvas(this.idPhaser, src)
              : scene.textures.addImage(this.idPhaser, src as unknown as HTMLImageElement); // fallback typé
          }
        }
        return this.idPhaser;
      }
    }

    // 2) Générer un placeholder minimal (8x8) — une seule fois par personnage
    if (!this.placeholderTextureKey) {
      this.placeholderTextureKey = `${this.idPhaser}__ph`;
      if (!scene.textures.exists(this.placeholderTextureKey)) {
        const g = scene.add.graphics();
        g.fillStyle(0x00cc66, 1);
        g.fillRect(0, 0, 8, 8);
        g.generateTexture(this.placeholderTextureKey, 8, 8);
        g.destroy();
      }
    }

    // 3) S'abonner : quand la vraie texture "idPhaser" arrive → basculer + recalibrer + (re)animer
    if (!this.textureAddHandler) {
      this.textureAddHandler = (key: string) => {
        if (key !== this.idPhaser || !this.sprite || !this.sprite.scene) return;
        const sc = this.sprite.scene;
        sc.textures.off(Phaser.Textures.Events.ADD, this.textureAddHandler!);
        this.textureAddHandler = undefined;

        // Basculer sur la vraie texture
        this.sprite.setTexture(this.idPhaser);

        // ⚠️ Recalibrer taille + corps AVANT de (re)créer / rejouer les animations
        this.sprite.setDisplaySize(this.width, this.height);
        this._syncBodyToDisplayTopLeft();

        // (Re)créer les animations à partir de this.animations si besoin
        this.ensureAnimations(sc);

        // Rejouer l'état courant (ou 'idle' par défaut)
        this.setAnimation(this.currentState || "idle");
      };
      scene.textures.on(Phaser.Textures.Events.ADD, this.textureAddHandler);
    }

    // 4) On utilise le placeholder en attendant
    return this.placeholderTextureKey!;
  }


  /**
   * Crée le sprite Phaser dans la scène.
   * Texture initiale = idPhaser (doit être préchargée comme image unique).
   */
  public createPhaserSprite(scene: Phaser.Scene) {
    this.scene = scene;

    // Utilise une texture sûre (réelle si dispo, sinon placeholder + bascule auto)
    const initialKey = this.resolveTextureOrPlaceholder(scene);

    const sprite = scene.physics.add.sprite(this.x, this.y, initialKey);
    this.sprite = sprite;
    sprite.setOrigin(0, 0);
    sprite.setDisplaySize(this.width, this.height);
    sprite.setCollideWorldBounds(true);
    this._syncBodyToDisplayTopLeft();

    const body = sprite.body as Phaser.Physics.Arcade.Body | undefined;
    if (body) {
      body.setSize(sprite.displayWidth, sprite.displayHeight, true);
      body.setOffset(0, 0);
    }

    // Si la vraie texture est déjà là, on découpe maintenant
    if (initialKey === this.idPhaser) {
      this.ensureAnimations(scene);
    }

    // Animation par défaut
    this.setAnimation("idle");

    // Admin : petit texte au-dessus
    if (this.adminMode) {
      const idText = scene.add.text(sprite.x, sprite.y - 15, this.idPhaser, {
        fontSize: "12px",
        color: "#ffffff",
        backgroundColor: "#00000088",
      });
      idText.setOrigin(0.5, 1);
      this.adminLabel = idText;

      const update = () => {
        if (!this.sprite || !this.adminLabel) return;
        this.adminLabel.x = this.sprite.x + this.sprite.displayWidth / 2;
        this.adminLabel.y = this.sprite.y - 2;
        this.adminLabel.setDepth((this.sprite.depth ?? 0) + 2);
      };
      this.boundUpdateForLabel = update;
      scene.events.on("update", update);
    }

    // Direction indicator (generated textures)
    const baseSize = Math.min(this.width, this.height);
    const dotSize = Math.max(2, baseSize * 0.1);

    const g = scene.add.graphics();
    g.fillStyle(0xff0000, 1);
    g.fillCircle(dotSize / 2, dotSize / 2, dotSize / 2);
    this.dotTextureKey = `${this.idPhaser}-direction-dot`;
    g.generateTexture(this.dotTextureKey, dotSize, dotSize);
    g.clear();

    const arrowWidth = Math.max(2, dotSize * 0.6);
    const arrowHeight = Math.max(4, dotSize * 2);
    g.fillStyle(0xff0000, 1);
    g.fillTriangle(arrowWidth / 2, 0, arrowWidth, arrowHeight, 0, arrowHeight);
    this.arrowTextureKey = `${this.idPhaser}-direction-arrow`;
    g.generateTexture(this.arrowTextureKey, arrowWidth, arrowHeight);
    g.destroy();

    const indicator = scene.add.image(0, 0, this.dotTextureKey);
    indicator.setOrigin(0.5, 1);
    indicator.setDepth((sprite.depth ?? 0) + 1);
    this.directionIndicator = indicator;

    // Keep indicator & admin label in sync with sprite lifecycle
    this.sprite.once("destroy", () => this.destroy(true));
    this.updateDirectionIndicator();
  }

  /** updatePhaserSprite : recale x,y logiques si besoin */
  public updatePhaserSprite() {
    if (!this.sprite) return;

    this.x = this.sprite.x;
    this.y = this.sprite.y;
    this.updateDirectionIndicator();

    // Ajuster l'orientation selon la vitesse
    const body = this.sprite.body as Phaser.Physics.Arcade.Body | undefined;
    if (body && body.velocity.x !== 0) {
      this.flipSprite(body.velocity.x < 0);
    }
  }

  private updateDirectionIndicator() {
    if (!this.directionIndicator || !this.sprite) return;

    // centre du sprite
    const cx = this.sprite.x + this.sprite.displayWidth / 2;
    const cy = this.sprite.y + this.sprite.displayHeight / 2;

    const halfW = this.sprite.displayWidth / 2;
    const halfH = this.sprite.displayHeight / 2;

    // vecteur de direction (repère écran : y vers le bas)
    const rad = Phaser.Math.DegToRad(this.angleDirection);
    const vx = Math.cos(rad);
    const vy = -Math.sin(rad);

    // bord dominant (horizontal vs vertical)
    const pad = 6; // marge en dehors du sprite
    let ix = cx;
    let iy = cy;

    if (Math.abs(vx) >= Math.abs(vy)) {
      // -> gauche/droite
      ix = cx + Math.sign(vx) * (halfW + pad);
      iy = cy;
    } else {
      // -> haut/bas
      ix = cx;
      iy = cy + Math.sign(vy) * (halfH + pad);
    }

    this.directionIndicator.setPosition(ix, iy);
    this.directionIndicator.setRotation(Phaser.Math.DegToRad(90 - this.angleDirection));
  }

  /** setAnimation : change l’anim ("idle","walking","jumping","winning", etc.) */
  /** setAnimation : change l’anim ("idle","walking","jumping","winning", etc.) */
  public setAnimation(state: string) {
    if (!this.sprite || !this.sprite.scene) return;

    this.currentState = state as CharacterState;
    const scene = this.sprite.scene;
    const animKey = `${state}_${this.idPhaser}`;

    const hasFrames = (key: string) => {
      const a = scene.anims.get(key) as any;
      return !!a && Array.isArray(a.frames) && a.frames.length > 0;
    };

    this.sprite.off("animationstart", (this as any)._onAnimStart, this);

    if (!hasFrames(animKey)) {
      // Tente de (re)générer si la vraie texture est dispo
      if (scene.textures.exists(this.idPhaser)) {
        this.ensureAnimations(scene);
      }
    }

    if (hasFrames(animKey)) {
      this.sprite.play(animKey, true);
      this.applyDisplaySizeAndBody();
      const onStart = () => this.applyDisplaySizeAndBody();
      (this as any)._onAnimStart = onStart;
      this.sprite.on("animationstart", onStart, this);
    } else {
      // Fallback : n’essaie PAS de forcer la texture idPhaser si elle n’existe pas encore
      if (scene.textures.exists(this.idPhaser)) {
        if (this.sprite.texture?.key !== this.idPhaser) this.sprite.setTexture(this.idPhaser);
        try { this.sprite.setFrame(0); } catch {/* ignore */ }
      }
      // On reste sur le placeholder sinon, mais on garde taille/corps alignés
      this.applyDisplaySizeAndBody();
    }
  }




  public setAngleDirection(angleDegree: number) {
    this.angleDirection = angleDegree;
    const isGoingLeft = Math.cos(Phaser.Math.DegToRad(this.angleDirection)) < 0;
    this.flipSprite(isGoingLeft);
    this.updateDirectionIndicator();
  }
  public getAngleDirection(): number {
    return this.angleDirection;
  }
  public addAngle(additionalAngle: number) {
    this.setAngleDirection((this.angleDirection + additionalAngle) % 360);
  }
  public flipAngle() {
    this.setAngleDirection((this.angleDirection + 180) % 360);
  }

  private hasGravity(): boolean {
    return !!this.scene && !!this.scene.physics && this.scene.physics.world.gravity.y !== 0;
  }

  // -------------------------
  // METHODES walk / jump
  // -------------------------

  /** Marche d'une distance (speedPxPerStep * unitNumber) */
  public async walk(unitNumber: number): Promise<void> {
    // If already moving or colliding, queue the movement
    if (this.isMoving || this.isColliding) {
      return new Promise<void>((resolve, reject) => {
        this.movementQueue.push({ unitNumber, resolve, reject });
      });
    }

    // Start the movement immediately
    return this.executeWalk(unitNumber);
  }

  private async executeWalk(unitNumber: number): Promise<void> {
    this.setMovingState(true);
    const distUnit = this.adjustDistanceForAngle(unitNumber);
    const distPx = this.width * distUnit;
    await this.doMovement(distPx, 1, false, "walking");
    this.setMovingState(false);

    // Process next movement in queue
    this.processMovementQueue();
  }

  private processMovementQueue(): void {
    if (this.movementQueue.length === 0) {
      return;
    }

    const nextMovement = this.movementQueue.shift();
    if (nextMovement) {
      this.executeWalk(nextMovement.unitNumber)
        .then(() => {
          nextMovement.resolve();
        })
        .catch((err) => {
          nextMovement.reject(err);
        });
    }
  }

  private adjustDistanceForAngle(baseDistance: number): number {
    const a = ((this.angleDirection % 360) + 360) % 360;
    return (a === 45 || a === 135 || a === 225 || a === 315)
      ? baseDistance * Math.SQRT2
      : baseDistance;
  }

  private calculateJumpSpeed(baseJumpSpeed: number, boostFactor: number, maxBoostMultiplier = 1.5) {
    const boost = 1 + (Math.log(1 + Math.max(0, boostFactor)) * (maxBoostMultiplier - 1));
    return baseJumpSpeed * boost;
  }

  /** distance: 0..10 (0 = 10 unités) */
  public async jump(distance: number): Promise<void> {
    if (this.isMoving || this.isColliding || !this.scene || !this.sprite) return;

    // SANS GRAVITÉ → comportement existant
    if (!this.hasGravity()) {
      this.setMovingState(true);
      const distPx = this.width * distance * 2;
      await this.doMovement(distPx, 4, true, "jumping");
      this.setMovingState(false);
      return;
    }

    // AVEC GRAVITÉ
    this.setMovingState(true);
    this.setAnimation("jumping");

    const dir = Math.cos(Phaser.Math.DegToRad(this.angleDirection)) >= 0 ? 1 : -1;
    const H_SPEED = this.width * 2;
    const V_SPEED = this.height * 4;
    const boostFactor = distance / 10;
    const jumpSpeed = this.calculateJumpSpeed(V_SPEED, boostFactor);

    this.sprite.setVelocity(dir * H_SPEED, -jumpSpeed);

    await new Promise<void>((resolve) => {
      const s = this.sprite!;
      const body = s.body as Phaser.Physics.Arcade.Body;

      const finish = () => {
        if (!this.sprite) { resolve(); return; }
        this.sprite.setVelocity(0, 0);
        this.setAnimation("idle");
        this.scene!.events.off("update", onUpdate);
        this.isMoving = false;
        resolve();
      };

      const onUpdate = () => {
        if (!this.sprite || !this.sprite.active) return finish();
        if (body.blocked.down || body.touching.down) finish();
      };

      this.scene!.events.on("update", onUpdate);
      this.scene!.time.delayedCall(3000, finish); // filet de sécurité
    });
  }

  /**
   * Gère un déplacement "distancePx" en utilisant la velocity.
   */
  private doMovement(
    distancePx: number,
    multiplierVelocity: number,
    collisionsOff: boolean,
    anim: CharacterState
  ): Promise<void> {
    if (!this.scene || !this.sprite) return Promise.resolve();

    this.setAnimation(anim);

    const body = this.sprite.body as Phaser.Physics.Arcade.Body | undefined;
    if (collisionsOff && body) body.checkCollision.none = true;

    const angleRad = Phaser.Math.DegToRad(this.angleDirection);
    const vx = Math.cos(angleRad) * this.width * multiplierVelocity;
    const vy = -Math.sin(angleRad) * this.width * multiplierVelocity;

    this.flipSprite(Math.cos(angleRad) < 0);

    const EPS = 1e-10;
    const velX = Math.abs(vx) < EPS ? 0 : vx;
    const velY = Math.abs(vy) < EPS ? 0 : vy;
    this.sprite.setVelocity(velX, velY);

    let velocityScalar = this.width * multiplierVelocity || 1;
    const neededMs = (distancePx / velocityScalar) * 1000 * 1.2;
    const TIMEOUT_MS = Math.min(neededMs, 30000);

    return new Promise<void>((resolve) => {
      let distTravelled = 0;
      let prevX = this.sprite!.x;
      let prevY = this.sprite!.y;

      const onUpdate = () => {
        if (!this.sprite) return cleanup();

        const dx = this.sprite.x - prevX;
        const dy = this.sprite.y - prevY;
        distTravelled += Math.hypot(dx, dy);
        prevX = this.sprite.x;
        prevY = this.sprite.y;

        if (distTravelled >= distancePx) cleanup();
      };

      const cleanup = () => {
        if (this.sprite && this.sprite.active) {
          this.sprite.setVelocity(0, 0);
          if (this.currentState === "walking" || this.currentState === "jumping") {
            this.setAnimation("idle");
          }
        }
        this.scene?.events.off("update", onUpdate);
        if (collisionsOff && body) body.checkCollision.none = false;
        if (timerEvent) timerEvent.remove(false);
        this.currentMovementCancel = null;
        resolve();
      };

      this.scene!.events.on("update", onUpdate);
      const timerEvent = this.scene!.time.delayedCall(TIMEOUT_MS, cleanup);

      // Permettre l’interruption (ex: collision)
      this.currentMovementCancel = cleanup;
    });
  }

  // ------------------------
  // ANIMATIONS / OUTILS
  // ------------------------
  private applyDisplaySizeAndBody() {
    if (!this.sprite) return;
    this.sprite.setDisplaySize(this.width, this.height);
    this._syncBodyToDisplayTopLeft(); // au lieu de setSize(displayWidth, ...)
  }



  public playWinAnimation() {
    this.setAnimation("winning");
  }

  public playCollisionAnimation() {
    if (this.isColliding) return;
    this.isColliding = true;
    this.stopMovement();
    this.setAnimation("collision");
    setTimeout(() => {
      this.setAnimation("idle");
      this.isColliding = false;
      this.processMovementQueue();
    }, 500);
  }

  private flipSprite(flip: boolean) {
    if (!this.sprite) return;
    (this.sprite as Phaser.Physics.Arcade.Sprite).setFlipX(flip);
  }

  // ------------------------
  // INVENTAIRE
  // ------------------------
  public pickUpObject(obj: GameObject): boolean {
    // run the object's tween + state change
    const ok = obj.pickUp();

    if (ok) {
      this.carriedObject.push(obj);
      // optional: play a sound or a pickup animation on the character
      // this.setAnimation("idle"); // or "pick" if you have it
    }
    return ok;
  }

  public dropObject(id: string, x: number, y: number) {
    const i = this.carriedObject.findIndex((o) => o.id === id);
    if (i === -1) return;
    this.carriedObject[i].drop(x, y);
    this.carriedObject.splice(i, 1);
  }

  public dropAllObjects(x: number, y: number) {
    for (const obj of this.carriedObject) obj.drop(x, y);
    this.carriedObject = [];
  }

  // ------------------------
  // SPEECH BUBBLE
  // ------------------------
  public say(message: string, duration: number = 2000) {
    if (!this.scene || !this.sprite) return;

    const sceneWidth = this.scene.sys.canvas.width;
    const sceneHeight = this.scene.sys.canvas.height;

    const bubbleWidth = Math.max(100, message.length * 8 + 20);
    const bubbleHeight = 50;
    const arrowSize = 8;
    const margin = 6;

    const container = this.scene.add.container(0, 0);
    const bubbleGfx = this.scene.add.graphics();
    const textObj = this.scene.add.text(0, 0, message, {
      fontSize: "14px",
      color: "#000000",
      wordWrap: { width: bubbleWidth - 10 },
    }).setOrigin(0.5, 0.5);

    container.add([bubbleGfx, textObj]);

    const drawBubble = (arrowAtBottom: boolean) => {
      bubbleGfx.clear();
      bubbleGfx.lineStyle(2, 0x000000, 1);
      bubbleGfx.fillStyle(0xffffff, 1);

      if (arrowAtBottom) {
        bubbleGfx.fillRoundedRect(-bubbleWidth / 2, -bubbleHeight, bubbleWidth, bubbleHeight, 10);
        bubbleGfx.strokeRoundedRect(-bubbleWidth / 2, -bubbleHeight, bubbleWidth, bubbleHeight, 10);
        bubbleGfx.fillTriangle(-arrowSize, 0, arrowSize, 0, 0, arrowSize);
        bubbleGfx.strokeTriangle(-arrowSize, 0, arrowSize, 0, 0, arrowSize);
        textObj.setPosition(0, -bubbleHeight / 2);
      } else {
        bubbleGfx.fillRoundedRect(-bubbleWidth / 2, 0, bubbleWidth, bubbleHeight, 10);
        bubbleGfx.strokeRoundedRect(-bubbleWidth / 2, 0, bubbleWidth, bubbleHeight, 10);
        bubbleGfx.fillTriangle(-arrowSize, 0, arrowSize, 0, 0, -arrowSize);
        bubbleGfx.strokeTriangle(-arrowSize, 0, arrowSize, 0, 0, -arrowSize);
        textObj.setPosition(0, bubbleHeight / 2);
      }
    };

    const updatePosition = () => {
      if (!this.sprite) return;

      let arrowBottom = true;
      let cx = this.sprite.getCenter().x;
      let cy = this.sprite.y - margin;

      const topOfBubble = cy - bubbleHeight;
      if (topOfBubble < 0) {
        arrowBottom = false;
        cy = this.sprite.y + this.sprite.displayHeight + margin;
      }

      const halfW = bubbleWidth / 2;
      if (cx - halfW < 0) cx = halfW;
      else if (cx + halfW > sceneWidth) cx = sceneWidth - halfW;

      if (!arrowBottom) {
        const bottomOfBubble = cy + bubbleHeight;
        if (bottomOfBubble > sceneHeight) {
          arrowBottom = true;
          cy = this.sprite.y - margin;
        }
      }

      drawBubble(arrowBottom);
      container.setPosition(cx, cy);
      container.setDepth((this.sprite.depth ?? 0) + 3);
    };

    updatePosition();
    this.scene.events.on("update", updatePosition);

    this.scene.time.delayedCall(duration, () => {
      container.destroy();
      this.scene?.events.off("update", updatePosition);
    });
  }

  // ------------------------
  // AUTOPILOT
  // ------------------------
  public startAutoPilot() {
    if (!this.scene) return;
    this.autoPilotEnabled = true;

    if (this.autoPilotTimer) this.autoPilotTimer.remove(false);
    this.autoPilotTimer = this.scene.time.addEvent({
      delay: 1000,
      callback: () => this.autoPilotMove(),
      loop: true,
    });
  }

  public stopAutoPilot() {
    this.autoPilotEnabled = false;
    if (this.scene && this.autoPilotTimer) {
      this.scene.time.removeEvent(this.autoPilotTimer);
      this.autoPilotTimer.remove(false);
      this.autoPilotTimer = undefined;
    }
    if (this.sprite) this.sprite.setVelocity(0, 0);
    this.setAnimation("idle");
  }

  private autoPilotMove() {
    if (!this.autoPilotEnabled || !this.sprite) return;
    const randomAngle = Phaser.Math.Between(0, 359);
    this.setAngleDirection(randomAngle);

    const randomDistance = Phaser.Math.Between(1, 3);
    this.walk(randomDistance).catch((err) => {
      console.error("AutoPilot walk error:", err);
    });
  }

  public moveTo(x: number, y: number) {
    this.sprite?.setPosition(x, y);
  }

  public changeSize(newWidth: number, newHeight: number) {
    this.sprite?.setDisplaySize(newWidth, newHeight);
    this._syncBodyToDisplayTopLeft(); // au lieu de setSize(displayWidth, ...)
    this.width = newWidth;
    this.height = newHeight;
  }


  // ------------------------------------------------------------------
  // ALWAYS-SLICE ANIMS: découpe l'image complète selon animations[]
  // et crée les animations Phaser (clé: `${state}_${idPhaser}`).
  // ------------------------------------------------------------------
  private ensureAnimations(scene: Phaser.Scene) {
    const tex = scene.textures.get(this.idPhaser) as Phaser.Textures.Texture | undefined;
    if (!tex) return;

    const list: any[] = Array.isArray(this.animations) ? this.animations : [];
    if (!list.length) return;

    // Enregistrer les frames déjà présentes pour rendre idempotent
    const existing = new Set<string>(Object.keys((tex as any).frames || {})); // inclut "__BASE"

    for (const a of list) {
      // support des champs côté backend
      const state: string = (a as any)?.name || (a as any)?.state;
      const count = Number((a as any)?.frame_count ?? (a as any)?.frameCount ?? 0);
      const fw = Number((a as any)?.frame_width ?? (a as any)?.frameWidth);
      const fh = Number((a as any)?.frame_height ?? (a as any)?.frameHeight);
      const ox = Number((a as any)?.offset_x ?? (a as any)?.offsetX ?? 0);
      const oy = Number((a as any)?.offset_y ?? (a as any)?.offsetY ?? 0);
      const rate = Number((a as any)?.frame_rate ?? (a as any)?.frameRate ?? 8);
      const repeat = Number.isFinite((a as any)?.repeat) ? Number((a as any)?.repeat) : -1;

      if (!state || !count || !fw || !fh) continue;

      // 1) Ajouter des sous-frames nommées `${state}_${i}` en bande horizontale (ox + i*fw, oy)
      for (let i = 0; i < count; i++) {
        const name = `${state}_${i}`;
        if (existing.has(name)) continue; // idempotent
        const x = ox + i * fw;
        const y = oy;
        try {
          tex.add(name, 0, x, y, fw, fh); // sourceIndex = 0 (image de base)
          existing.add(name);
        } catch (err) {
          // eslint-disable-next-line no-console
          console.warn(`[slice-frames] cannot add frame ${name} @ (${x},${y},${fw},${fh})`, err);
        }
      }

      // 2) Créer (ou réutiliser) l'animation Phaser pour cet état
      const animKey = `${state}_${this.idPhaser}`;
      if (!scene.anims.exists(animKey)) {
        const frames = new Array(count).fill(0).map((_, i) => ({
          key: this.idPhaser,
          frame: `${state}_${i}`,
        }));
        if (frames.length) {
          scene.anims.create({
            key: animKey,
            frames,
            frameRate: rate || 8,
            repeat,
          });
        }
      }
    }
  }

  // Dans class Character
  private _syncBodyToDisplayTopLeft() {
    if (!this.sprite) return;
    const body = this.sprite.body as Phaser.Physics.Arcade.Body | undefined;
    if (!body) return;

    // convertir taille affichée -> taille non-scalée attendue par Arcade
    const sx = this.sprite.scaleX || 1;
    const sy = this.sprite.scaleY || 1;
    const unscaledW = this.sprite.displayWidth / sx;
    const unscaledH = this.sprite.displayHeight / sy;

    body.setSize(unscaledW, unscaledH, false); // false = ne pas recentrer
    body.setOffset(0, 0);                      // top-left, cohérent avec setOrigin(0,0)
  }


}
